import { Issuer, NewsItem } from "api/generated/cms";
import { CmsTag, mapCmsTags } from "./mapTags";
import { parseCmsId } from "./parseCmsId";

/**
 * One news item, lifted out of the issuer it arrived on so it can be listed
 * alongside every other issuer's news.
 */
export interface CmsNewsArticle {
  /** Addresses the article in a list and in the article route. */
  key: string;
  /** The CMS id, or null for an article the CMS left unidentified — that one
   *  can be listed but has no page of its own. */
  articleId: number | null;
  issuerId: number;
  issuerName: string;
  issuerLogo?: string;
  title: string;
  /** ISO `YYYY-MM-DD`, or whatever the editor typed — the CMS does not validate. */
  date: string;
  tags: CmsTag[];
  /** Teaser copy. `excerpt` is the field this replaces and is on its way out. */
  ingress?: string;
  /** Plain-text body. Empty until the CMS editors fill it in; an article
   *  without it has nothing to show on its own page. */
  text?: string;
  image?: string;
  /** The issuer's own article page, when they published it themselves. */
  externalUrl?: string;
}

/** How long a title may run in a URL before it is cut. */
const MAX_SLUG_LENGTH = 80;

/**
 * Title → URL segment. Swedish vowels fold to their base letter rather than
 * being dropped, so "Räntor" stays readable as "rantor".
 */
const slugify = (title: string): string =>
  title
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_SLUG_LENGTH)
    .replace(/-+$/, "");

/**
 * An article's URL segment: `<issuerId>-<articleId>-<title slug>`.
 *
 * Only the two leading numbers identify the article — the slug is decoration,
 * so retitling changes the URL without breaking the old one. The issuer id is
 * carried alongside the article id so an article that has rotated out of the
 * feed can still redirect to the issuer it belonged to, and so a collision is
 * impossible if the CMS ids turn out to be scoped per issuer rather than
 * global.
 */
export const newsArticleKey = (
  issuerId: number,
  articleId: number,
  title: string
): string => `${issuerId}-${articleId}-${slugify(title)}`;

/** Reads the identifying numbers back out of a key, ignoring the slug. */
export const parseNewsArticleKey = (
  key: string
): { issuerId: number; articleId: number } | null => {
  const [issuer, article] = key.split("-");
  const issuerId = Number(issuer);
  const articleId = Number(article);
  if (
    !issuer ||
    !article ||
    !Number.isFinite(issuerId) ||
    !Number.isFinite(articleId)
  ) {
    return null;
  }
  return { issuerId, articleId };
};

/** Whether a route key addresses this article, whatever slug it carries. */
export const matchesNewsArticle = (
  article: CmsNewsArticle,
  key: string
): boolean => {
  const parsed = parseNewsArticleKey(key);
  return (
    parsed !== null &&
    parsed.issuerId === article.issuerId &&
    parsed.articleId === article.articleId
  );
};

/** Later dates first; items the CMS left undated sink to the bottom. */
const byDateDescending = (a: CmsNewsArticle, b: CmsNewsArticle) =>
  (b.date || "").localeCompare(a.date || "");

const toArticle = (
  item: NewsItem,
  issuer: Issuer,
  issuerId: number
): CmsNewsArticle | null => {
  const title = item.title?.trim();
  if (!title) {
    return null;
  }
  const articleId = parseCmsId(item.id);
  return {
    key:
      articleId === null
        ? `${issuerId}-${slugify(title)}`
        : newsArticleKey(issuerId, articleId, title),
    articleId,
    issuerId,
    issuerName: issuer.name ?? "",
    issuerLogo: issuer.logoImage ?? undefined,
    title,
    date: item.date ?? "",
    tags: mapCmsTags(item.tags),
    // `ingress` supersedes `excerpt`; both hold the same value today and
    // `excerpt` is being removed, so read it only as a fallback.
    ingress: item.ingress ?? item.excerpt ?? undefined,
    text: item.text?.trim() || undefined,
    // Same story for `imageUrl` over `image`.
    image: item.imageUrl ?? item.image ?? undefined,
    externalUrl: item.newsUrl ?? undefined,
  };
};

/** Every news item an issuer published, featured first as the CMS ordered it. */
export const issuerNewsItems = (issuer: Issuer): NewsItem[] => [
  ...(issuer.news?.featured ? [issuer.news.featured] : []),
  ...(issuer.news?.secondary ?? []),
];

/** Flattens every issuer's news into one feed, newest first. */
export const toNewsFeed = (issuers: Issuer[]): CmsNewsArticle[] =>
  issuers
    .flatMap((issuer) => {
      const issuerId = parseCmsId(issuer.id);
      if (issuerId === null) {
        return [];
      }
      return issuerNewsItems(issuer).flatMap((item) => {
        const article = toArticle(item, issuer, issuerId);
        return article ? [article] : [];
      });
    })
    .sort(byDateDescending);

/** Distinct tag labels across the feed, alphabetical — the filter's options. */
export const newsFeedTagLabels = (articles: CmsNewsArticle[]): string[] =>
  Array.from(
    new Set(articles.flatMap((article) => article.tags.map((tag) => tag.label)))
  ).sort((a, b) => a.localeCompare(b, "sv"));
